import React, { Fragment } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Accordion,
  AccordionDetails,
  Alert,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import UpdateIcon from '@mui/icons-material/Update'
import ErrorIcon from '@mui/icons-material/Error'

const ImportResultsTable = ({ comparisonData }) => {
  const { updates = [], inserts = [], errors = [] } = comparisonData || {}

  // Common table styling for sticky headers
  const stickyHeaderStyle = {
    '& .MuiTableHead-root': {
      backgroundColor: 'background.paper',
      '& .MuiTableCell-root': {
        backgroundColor: 'background.paper',
        borderBottom: '2px solid',
        borderColor: 'divider',
      },
    },
  }

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No changes
        </Typography>
      )
    }

    return Object.entries(changes).map(([field, change]) => (
      <Box
        key={field}
        sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}
      >
        <Typography
          sx={{ textTransform: 'capitalize' }}
          variant="body2"
          fontWeight="medium"
          color="primary"
        >
          {field.replace(/\./g, ' ').replace(/_/g, ' ')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Old Value:
            </Typography>
            <Alert severity="error">
              <Typography variant="body2" color="text.secondary">
                {`${change.old ?? ''}` || 'N/A'}
              </Typography>
            </Alert>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              New Value:
            </Typography>
            <Alert severity="success">
              <Typography variant="body2" color="text.secondary">
                {`${change.new ?? ''}` || 'N/A'}
              </Typography>
            </Alert>
          </Box>
        </Box>
      </Box>
    ))
  }

  const renderUpdatesTable = () => {
    if (updates.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon
            sx={{ fontSize: 48, color: 'success.main', mb: 2 }}
          />
          <Typography variant="h6" color="success.main">
            No Updates Required
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All records are up to date
          </Typography>
        </Box>
      )
    }

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small" sx={stickyHeaderStyle}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Row #</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tax Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Changes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {updates.map((update) => (
              <Fragment key={`${update.tax_code}-${update.row_number}`}>
                <TableRow
                  hover
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: 'inherit',
                  }}
                >
                  <TableCell>
                    <Chip
                      label={update.row_number}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {update.tax_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${
                        Object.keys(update.changes || {}).length
                      } change(s)`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                    <Accordion
                      expanded={true}
                      sx={{
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        bgcolor: 'transparent',
                      }}
                    >
                      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        <Box sx={{ pl: 2 }}>
                          {renderChanges(update.changes)}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const renderInsertsTable = () => {
    if (inserts.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon
            sx={{ fontSize: 48, color: 'success.main', mb: 2 }}
          />
          <Typography variant="h6" color="success.main">
            No New Records
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No new records to insert
          </Typography>
        </Box>
      )
    }

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small" sx={stickyHeaderStyle}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Row #</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tax Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inserts.map((insert, index) => (
              <TableRow key={`${insert.tax_code}-${insert.row_number}`} hover>
                <TableCell>
                  <Chip
                    label={insert.row_number}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {insert.tax_code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label="New Record"
                    size="small"
                    color="primary"
                    icon={<CheckCircleIcon />}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const renderErrorsTable = () => {
    if (errors.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon
            sx={{ fontSize: 48, color: 'success.main', mb: 2 }}
          />
          <Typography variant="h6" color="success.main">
            No Validation Errors
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All records passed validation
          </Typography>
        </Box>
      )
    }

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small" sx={stickyHeaderStyle}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Row #</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tax Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Error Count</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.map((errorRow) => (
              <Fragment key={`${errorRow.tax_code}-${errorRow.row_number}`}>
                <TableRow
                  hover
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: 'inherit',
                  }}
                >
                  <TableCell>
                    <Chip
                      label={errorRow.row_number}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {errorRow.tax_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${errorRow.errors.length} error(s)`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label="View Errors"
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                    <Accordion
                      expanded={true}
                      sx={{
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        bgcolor: 'transparent',
                      }}
                    >
                      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        <Box sx={{ pl: 2 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            {errorRow.errors.map((error, index) => (
                              <Alert
                                key={index}
                                severity="error"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {error}
                              </Alert>
                            ))}
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  if (!comparisonData) {
    return null
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Errors Section - Show first if there are errors */}
      {errors.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ErrorIcon color="error" />
            <Typography variant="h6" color="error.main">
              Validation Errors ({errors.length})
            </Typography>
          </Box>
          {renderErrorsTable()}
        </Box>
      )}

      {updates.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <UpdateIcon color="primary" />
            <Typography variant="h6" color="primary.main">
              Records to Update ({updates.length})
            </Typography>
          </Box>
          {renderUpdatesTable()}
        </Box>
      )}

      {inserts.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CheckCircleIcon color="primary" />
            <Typography variant="h6" color="primary.main">
              New Records to Insert ({inserts.length})
            </Typography>
          </Box>
          {renderInsertsTable()}
        </Box>
      )}
    </Box>
  )
}

export default ImportResultsTable
